'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import useFetchCoatingPricing from '../../../hooks/useFetchCoatingPricing';
import { toast } from 'react-toastify';

// PATCH request for a single row
const useUpdateCoatingPricing = () => {
  const updatePrice = async (id, fbePricePerFoot, aroPricePerFoot) => {
    try {
      const patchDoc = [
        { op: 'replace', path: '/fbePricePerFoot', value: fbePricePerFoot },
        { op: 'replace', path: '/aroPricePerFoot', value: aroPricePerFoot }
      ];

      const response = await axios.patch(
        `/api/PipelinePricing/coating/update-pricing/${id}`,
        patchDoc,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
        }
      );

      return { success: true, message: response.data };
    } catch (error) {
      console.error('Update failed:', error);
      return { success: false, message: error?.response?.data || error.message };
    }
  };

  return { updatePrice };
};

const CoatingPricingGrid = () => {
  const { coatingPricing, loading, error } = useFetchCoatingPricing();
  const { updatePrice } = useUpdateCoatingPricing();
  const [coatingData, setCoatingData] = useState([]);
  const [editedRows, setEditedRows] = useState(new Map());

  useEffect(() => {
    if (!loading && !error) {
      setCoatingData(coatingPricing);
      setEditedRows(new Map());
    }
  }, [coatingPricing, loading, error]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Pipe OD',
        field: 'pipeOD',
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#333' },
      },
      {
        headerName: 'FBE $/ft',
        field: 'fbePricePerFoot',
        editable: true,
        flex: 1,
      },
      {
        headerName: 'ARO $/ft',
        field: 'aroPricePerFoot',
        editable: true,
        flex: 1,
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const updated = [...coatingData];
    updated[event.node.rowIndex] = event.data;
    setCoatingData(updated);

    if (event.data.id != null) {
      const newEdited = new Map(editedRows);
      newEdited.set(event.data.id, {
        fbePricePerFoot: event.data.fbePricePerFoot,
        aroPricePerFoot: event.data.aroPricePerFoot,
      });
      setEditedRows(newEdited);
    } else {
      console.warn('Missing ID on edited row');
    }
  };

  const handleUpdatePricing = async () => {
    if (editedRows.size === 0) {
      toast.info('No changes to update.');
      return;
    }

    let allSucceeded = true;
    for (const [id, prices] of editedRows.entries()) {
      const result = await updatePrice(id, prices.fbePricePerFoot, prices.aroPricePerFoot);
      if (!result.success) {
        allSucceeded = false;
        toast.error(`Failed to update row ${id}: ${result.message}`);
      }
    }

    if (allSucceeded) {
      toast.success('Coating prices updated successfully!');
      setEditedRows(new Map());
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Coating Pricing Table
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Box className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
              <AgGridReact
                rowData={coatingData}
                columnDefs={columnDefs}
                onCellValueChanged={handleGridEdit}
                defaultColDef={{ resizable: true }}
                domLayout="normal"
                getRowNodeId={(data) => data.id}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleUpdatePricing}>
              Update Pricing
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CoatingPricingGrid;
